import { useEffect } from "react";

const NAVBAR_SELECTOR = ".MuiAppBar-root";
const CLIENT_HEADER_Z_INDEX = "4";
const HEADER_Z_INDEX = "3";

const navbarBottom = () => {
  const appBarElement = document.querySelector(NAVBAR_SELECTOR);
  return appBarElement
    ? Math.round(appBarElement.getBoundingClientRect().bottom)
    : 0;
};

const columnBodyCells = ({ scrollContainer, index }) =>
  [...scrollContainer.querySelectorAll("tbody tr")].flatMap((row) => {
    const cell = row.children[index];
    if (!cell || cell.colSpan > 1) return [];
    return [cell];
  });

const clearFixedHeader = ({ headerRow, headerCells, widthLocks }) => {
  if (headerRow) headerRow.style.height = "";
  headerCells.forEach((cell) => {
    cell.style.position = "";
    cell.style.top = "";
    cell.style.left = "";
    cell.style.width = "";
    cell.style.zIndex = "";
    cell.style.clipPath = "";
    cell.style.margin = "";
  });
  widthLocks.forEach((cell) => {
    if (cell) cell.style.minWidth = "";
  });
  widthLocks.length = 0;
};

const lockColumnWidths = ({ headerCells, scrollContainer, widthLocks }) => {
  const referenceCells = headerCells.map(
    (_, index) => columnBodyCells({ scrollContainer, index })[0]
  );
  const measuredWidths = referenceCells.map((cell, index) =>
    Math.max(
      cell?.getBoundingClientRect().width ?? 0,
      parseFloat(widthLocks[index]?.style.minWidth) || 0
    )
  );

  referenceCells.forEach((cell, index) => {
    const previousLock = widthLocks[index];
    if (previousLock && previousLock !== cell) previousLock.style.minWidth = "";
    widthLocks[index] = cell;
    if (cell) cell.style.minWidth = `${measuredWidths[index]}px`;
  });
};

const applyFixedHeader = ({
  headerRow,
  headerCells,
  scrollContainer,
  widthLocks,
}) => {
  if (!headerRow || headerCells.length === 0) return;

  const top = navbarBottom();
  const containerRect = scrollContainer.getBoundingClientRect();
  const headerHeight = Math.round(headerRow.getBoundingClientRect().height);
  const clientCells = columnBodyCells({ scrollContainer, index: 0 });
  const clientRect = (clientCells[0] || headerCells[0]).getBoundingClientRect();
  const clientWidth = clientRect.width;
  const clientRight = containerRect.left + clientWidth;
  const placements = headerCells.map((_, index) => {
    const isClientColumn = index === 0;
    const referenceCell = isClientColumn ? clientCells[0] : widthLocks[index];
    const sourceRect = (
      referenceCell || headerCells[index]
    ).getBoundingClientRect();
    const width = isClientColumn ? clientWidth : sourceRect.width;
    const left = isClientColumn ? containerRect.left : sourceRect.left;
    const clipLeft = Math.max(
      0,
      (isClientColumn ? containerRect.left : clientRight) - left
    );
    const clipRight = Math.max(0, left + width - containerRect.right);
    return { isClientColumn, width, left, clipLeft, clipRight };
  });

  headerRow.style.height = `${headerHeight}px`;
  placements.forEach(
    ({ isClientColumn, width, left, clipLeft, clipRight }, index) => {
      const cell = headerCells[index];
      cell.style.position = "fixed";
      cell.style.top = `${top}px`;
      cell.style.left = `${left}px`;
      cell.style.width = `${width}px`;
      cell.style.margin = "0";
      cell.style.zIndex = isClientColumn
        ? CLIENT_HEADER_Z_INDEX
        : HEADER_Z_INDEX;
      cell.style.clipPath = `inset(0px ${clipRight}px 0px ${clipLeft}px)`;
    }
  );
};

const usePageStickyTableHeader = ({
  tableWrapperRef,
  tableHeadRef,
  scrollContainerRef,
  enabled,
}) => {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;
    const tableElement = tableWrapperRef.current;
    const tableHeadElement = tableHeadRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!tableElement || !tableHeadElement || !scrollContainer)
      return undefined;

    const headerRow = tableHeadElement.querySelector("tr");
    const headerCells = [...tableHeadElement.querySelectorAll("th")];
    if (!headerRow || headerCells.length === 0) return undefined;

    let isPinned = false;
    let isPastHeader = false;
    let isInsideTable = true;
    const widthLocks = [];
    let observedNavbarBottom = navbarBottom();
    let observedHeaderHeight = Math.round(
      headerRow.getBoundingClientRect().height
    );
    let topObserver;
    let tableObserver;

    const setPinned = (nextPinned) => {
      if (nextPinned === isPinned) return;
      isPinned = nextPinned;
      if (nextPinned) {
        lockColumnWidths({ headerCells, scrollContainer, widthLocks });
        applyFixedHeader({
          headerRow,
          headerCells,
          scrollContainer,
          widthLocks,
        });
        return;
      }
      clearFixedHeader({ headerRow, headerCells, widthLocks });
    };

    const updatePinnedState = () => {
      setPinned(isPastHeader && isInsideTable);
    };

    const connectObservers = () => {
      topObserver?.disconnect();
      tableObserver?.disconnect();
      observedNavbarBottom = navbarBottom();
      observedHeaderHeight = Math.round(
        headerRow.getBoundingClientRect().height || observedHeaderHeight
      );
      topObserver = new IntersectionObserver(
        ([entry]) => {
          isPastHeader =
            !entry.isIntersecting &&
            entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0);
          updatePinnedState();
        },
        {
          rootMargin: `-${observedNavbarBottom}px 0px 0px 0px`,
          threshold: 0,
        }
      );
      tableObserver = new IntersectionObserver(
        ([entry]) => {
          const rootTop = entry.rootBounds?.top ?? 0;
          const isAboveTable =
            !entry.isIntersecting && entry.boundingClientRect.bottom <= rootTop;
          isInsideTable = !isAboveTable;
          updatePinnedState();
        },
        {
          rootMargin: `-${observedNavbarBottom + observedHeaderHeight}px 0px 0px 0px`,
          threshold: 0,
        }
      );
      topObserver.observe(headerRow);
      tableObserver.observe(tableElement);
    };

    const onHorizontalScroll = () => {
      if (!isPinned) return;
      applyFixedHeader({ headerRow, headerCells, scrollContainer, widthLocks });
    };

    const onGeometryChange = () => {
      const nextNavbarBottom = navbarBottom();
      const nextHeaderHeight = Math.round(
        headerRow.getBoundingClientRect().height
      );
      if (
        nextNavbarBottom !== observedNavbarBottom ||
        nextHeaderHeight !== observedHeaderHeight
      ) {
        connectObservers();
      }
      if (isPinned) {
        lockColumnWidths({ headerCells, scrollContainer, widthLocks });
        applyFixedHeader({
          headerRow,
          headerCells,
          scrollContainer,
          widthLocks,
        });
      }
    };

    connectObservers();
    scrollContainer.addEventListener("scroll", onHorizontalScroll, {
      passive: true,
    });
    window.addEventListener("resize", onGeometryChange);
    const resizeObserver = new ResizeObserver(onGeometryChange);
    resizeObserver.observe(tableElement);
    resizeObserver.observe(scrollContainer);
    resizeObserver.observe(headerRow);
    const appBarElement = document.querySelector(NAVBAR_SELECTOR);
    if (appBarElement) resizeObserver.observe(appBarElement);

    return () => {
      topObserver?.disconnect();
      tableObserver?.disconnect();
      resizeObserver.disconnect();
      scrollContainer.removeEventListener("scroll", onHorizontalScroll);
      window.removeEventListener("resize", onGeometryChange);
      clearFixedHeader({ headerRow, headerCells, widthLocks });
    };
  }, [enabled, scrollContainerRef, tableHeadRef, tableWrapperRef]);
};

export default usePageStickyTableHeader;
